/** @jsxImportSource @emotion/react */

import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import { DragLocationHistory } from "@atlaskit/pragmatic-drag-and-drop/dist/types/internal-types";
import { draggable } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { disableNativeDragPreview } from "@atlaskit/pragmatic-drag-and-drop/element/disable-native-drag-preview";
import { preventUnhandled } from "@atlaskit/pragmatic-drag-and-drop/prevent-unhandled";
import { css } from "@emotion/react";
import { useSignal, useSignalEffect } from "@preact/signals-react";
import { isNil } from "lodash-es";
import React, { CSSProperties, useEffect, useRef } from "react";
import { idleState } from "../library/designer";

const KEY_RESIZABLE_WIDTH_HEIGHT = "resizable-width-height";

function setResizableWidthHeightToStorage({
  id,
  width,
  height,
}: {
  id?: string | null;
  width?: number | null;
  height?: number | null;
}): void {
  if (!id) return;

  try {
    const resizableWidthHeightString = localStorage.getItem(KEY_RESIZABLE_WIDTH_HEIGHT);
    const resizableWidthHeight = resizableWidthHeightString ? JSON.parse(resizableWidthHeightString) : {};
    resizableWidthHeight[id] = { width, height };
    localStorage.setItem(KEY_RESIZABLE_WIDTH_HEIGHT, JSON.stringify(resizableWidthHeight));
  } catch (error) {
    console.error("Error setting resizable width and height to localStorage", error);
  }
}

/**
 * tries to load saved width and height values from localStorage using a given id.
 * If the values are missing, invalid, or smaller than optional default values, it
 * safely returns the defaults instead (or 0 if no defaults are provided).
 */
function getResizableWidthHeightFromStorage({
  id,
  defaultWidth,
  defaultHeight,
}: {
  id?: string | null;
  defaultWidth?: number | null;
  defaultHeight?: number | null;
}): { width: number; height: number } {
  const fallbackWidth = typeof defaultWidth === "number" && !Number.isNaN(defaultWidth) ? defaultWidth : 0;
  const fallbackHeight = typeof defaultHeight === "number" && !Number.isNaN(defaultHeight) ? defaultHeight : 0;

  if (!id) return { width: fallbackWidth, height: fallbackHeight };

  try {
    const resizableWidthHeightString = localStorage.getItem(KEY_RESIZABLE_WIDTH_HEIGHT);
    if (!resizableWidthHeightString) return { width: fallbackWidth, height: fallbackHeight };

    const resizableWidthHeight = JSON.parse(resizableWidthHeightString);
    if (!resizableWidthHeight[id]) return { width: fallbackWidth, height: fallbackHeight };

    const { width, height } = resizableWidthHeight[id];

    const isWidthValid = typeof width === "number" && !Number.isNaN(width);
    const isHeightValid = typeof height === "number" && !Number.isNaN(height);

    if (!isWidthValid || !isHeightValid) return { width: fallbackWidth, height: fallbackHeight };

    return {
      width: width < fallbackWidth ? fallbackWidth : width,
      height: height < fallbackHeight ? fallbackHeight : height,
    };
  } catch (error) {
    console.error("Error parsing resizable width and height from localStorage", error);
    return { width: fallbackWidth, height: fallbackHeight };
  }
}

function getProposedSize(
  startSize: number,
  options: {
    location: DragLocationHistory;
    resizableConfig: IResizableConfig;
  }
): number {
  if (startSize === 0) return startSize;
  const {
    location,
    resizableConfig: { direction = "right" },
  } = options;
  if (direction === "top" || direction === "bottom") {
    const diffX = location.current.input.clientY - location.initial.input.clientY;
    const proposedHeight = direction === "bottom" ? startSize + diffX : startSize - diffX;

    return proposedHeight > 1 ? proposedHeight : 1;
  }
  const diffX = location.current.input.clientX - location.initial.input.clientX;
  const proposedWidth = direction === "right" ? startSize + diffX : startSize - diffX;

  return proposedWidth > 1 ? proposedWidth : 1;
}

interface IResizableConfig {
  startWidth?: string;
  minWidth?: number;
  maxWidth?: number;
  startHeight?: string;
  minHeight?: number;
  maxHeight?: number;
  zIndex?: number;

  direction?: "right" | "left" | "top" | "bottom";
  dragBorderColor?: string;
}

const containerStyles = ({ direction = "right" }: IResizableConfig) => {
  const map = {
    right: "row",
    left: "row-reverse",
    bottom: "column",
    top: "column-reverse",
  };

  return css({
    flexShrink: "0",
    flexGrow: "0",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: map[direction],
  });
};

const contentStyles = css({
  flexGrow: "1",
  flexShrink: "1",
  width: "var(--local-resizing-width, var(--local-initial-width))",
  height: "var(--local-resizing-height, var(--local-initial-height))",
});

// Quite a large draggable area,
// but the line itself is fairly small
const dividerStyles = ({ direction = "right", zIndex = 1200, dragBorderColor }: IResizableConfig) => {
  return css({
    cursor: direction === "right" || direction === "left" ? "ew-resize" : "ns-resize",
    flexGrow: "0",
    flexShrink: "0",
    position: "relative",
    background: "transparent",
    "&::before": (direction === "right" || direction === "top") && {
      content: '""',
      position: "absolute",
      top: 0,
      bottom: 0,
      width: direction === "right" ? "4px" : "100%",
      height: direction === "right" ? "100%" : "-4px",
    },
    "&::after": (direction === "left" || direction === "bottom") && {
      content: '""',
      position: "absolute",
      top: direction === "bottom" ? "-4px" : 0,
      bottom: 0,
      width: direction === "left" ? "4px" : "100%",
      height: direction === "left" ? "100%" : "-4px",
    },
    // default (unhovered): no delay, fast 0.1s ease‑out
    "&::before, &::after": {
      transition: "background-color 0.1s ease-out 0s",
      zIndex,
    },

    // on hover: wait 0.3s, then 0.1s ease‑in to red
    "&:hover::before, &:hover::after": {
      transition: "background-color 0.1s ease-in 0.3s",
      backgroundColor: dragBorderColor ?? "var(--md-sys-color-surface-focused, #EFF2F9)",
      zIndex,
    },
  });
};

// Preventing items getting :hover effects during a drag
const noPointerEventsStyles = css({
  pointerEvents: "none",
});

const backgroundDrag = ({ dragBorderColor = "transparent" }: IResizableConfig) =>
  css({
    "&::before": {
      backgroundColor: dragBorderColor,
    },
    "&::after": {
      backgroundColor: dragBorderColor,
    },
  });

function convertStringSizeToNumber(
  size: string,
  direction: "right" | "left" | "top" | "bottom" = "right",
  parent?: HTMLElement | null
): number {
  const dimensionDirection = direction === "bottom" || direction === "top" ? "vertical" : "horizontal";
  const referenceElement = parent ?? document.body;
  if (size.includes("%")) {
    const parentSize =
      dimensionDirection === "horizontal" ? referenceElement.clientWidth : referenceElement.clientHeight;
    const ratio = Number(size.split("%")[0]) / 100;
    return parentSize * ratio;
  }

  if (size.includes("px")) {
    return Number(size.split("px")[0]);
  }

  return 0;
}

export default function useResizable(resizableConfig: IResizableConfig, parent?: HTMLElement | null, id?: string) {
  const defaultWidth = convertStringSizeToNumber(resizableConfig.startWidth ?? "", resizableConfig.direction, parent);
  const defaultHeight = convertStringSizeToNumber(resizableConfig.startHeight ?? "", resizableConfig.direction, parent);
  const { width: restoredWidth, height: restoredHeight } = getResizableWidthHeightFromStorage({
    id,
    defaultWidth,
    defaultHeight,
  });

  const initialWidth = useSignal<number>(restoredWidth);
  const initialHeight = useSignal<number>(restoredHeight);

  const contentRef = useRef<HTMLDivElement | null>(null);
  const dividerRef = useRef<HTMLDivElement | null>(null);
  const dragState = useSignal(idleState);

  // we need to track direction as it can change after the initial render
  const directionSignal = useSignal(resizableConfig.direction ?? "right");

  // we update the direction signal when the direction in resizableConfig changes
  useEffect(() => {
    directionSignal.value = resizableConfig.direction ?? "right";
  }, [directionSignal, resizableConfig.direction]);

  useSignalEffect(() => {
    if (isNil(dividerRef.current)) return;

    // we need to use the updated direction signal, changes in resizableConfig doesn't trigger a signal effect
    // so we need to use the updated signal value for the latest direction
    const updatedResizableConfig = { ...resizableConfig, direction: directionSignal.value };

    const divider = dividerRef.current;

    return combine(
      draggable({
        element: divider,
        onGenerateDragPreview: ({ nativeSetDragImage }) => {
          // we will be moving the line to indicate a drag
          // we can disable the native drag preview
          disableNativeDragPreview({ nativeSetDragImage });
          // we don't want any native drop animation for when the user
          // does not drop on a drop target. we want the drag to finish immediately
          preventUnhandled.start();
        },
        onDragStart() {
          dragState.value = { type: "dragging" };
        },
        onDrag({ location }) {
          if (initialWidth.value !== 0)
            contentRef.current?.style.setProperty(
              "--local-resizing-width",
              `${getProposedSize(initialWidth.value, { location, resizableConfig: updatedResizableConfig })}px`
            );

          if (initialHeight.value !== 0)
            contentRef.current?.style.setProperty(
              "--local-resizing-height",
              `${getProposedSize(initialHeight.value, { location, resizableConfig: updatedResizableConfig })}px`
            );
        },
        onDrop({ location }) {
          preventUnhandled.stop();
          dragState.value = idleState;

          initialWidth.value = getProposedSize(initialWidth.value, {
            location,
            resizableConfig: updatedResizableConfig,
          });
          initialHeight.value = getProposedSize(initialHeight.value, {
            location,
            resizableConfig: updatedResizableConfig,
          });
          contentRef.current?.style.removeProperty("--local-resizing-width");
          contentRef.current?.style.removeProperty("--local-resizing-height");
          setResizableWidthHeightToStorage({
            id,
            width: initialWidth.value,
            height: initialHeight.value,
          });
        },
      })
    );
  });

  const Resizable = (reactComponent: React.ReactNode) => {
    if (resizableConfig) {
      return (
        <div css={containerStyles(resizableConfig)} data-testid="resizable-divider">
          {/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */}
          <div
            ref={contentRef}
            css={[contentStyles, dragState.value.type === "dragging" ? noPointerEventsStyles : undefined]}
            // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
            style={
              {
                "--local-initial-width": initialWidth.value ? `${initialWidth.value}px` : "100%",
                "--local-initial-height": initialHeight.value ? `${initialHeight.value}px` : "100%",
              } as CSSProperties
            }
          >
            {reactComponent}
          </div>
          <div
            css={[
              dividerStyles(resizableConfig),
              // Disabling the cursor on the sidebar line while dragging
              // Otherwise the cursor can flash between resizing and the default cursor repeatedly
              dragState.value.type === "dragging" ? noPointerEventsStyles : undefined,
              dragState.value.type === "dragging" ? backgroundDrag(resizableConfig) : undefined,
              dragState.value.type === "dragging" &&
                css({
                  "&::before, &::after": {
                    backgroundColor: resizableConfig.dragBorderColor ?? "var(--md-sys-color-surface-focused, #EFF2F9)",
                    zIndex: resizableConfig.zIndex ?? 1200,
                  },
                }),
            ]}
            ref={dividerRef}
          />
        </div>
      );
    } else {
      return reactComponent;
    }
  };

  return {
    resizable: Resizable,
  };
}
